import { PostFeedItemT } from 'types/shared/core/Feeds';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { Avatar, ButtonBase, IconButton, Stack, Typography, useTheme } from '@mui/material';
import React, { ComponentType, useRef } from 'react';
import PostFeedImages from './PostFeedImages';
import { ExtendedPostTagWrapper } from 'presenters/Post/PostTagWrapper';
import { observer } from 'mobx-react-lite';
import ContentDateTime from 'presenters/common/ContentDateTime';
import { DUMMY_DATETIME } from 'constants/frontend/shared/values';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { PostT } from 'types/shared/core/Post';
import NonDefaultStateIndicator from 'presenters/UI/NonDefaultStateIndicator';

export type PostFeedPropsT = {
  perspective: 'teacherSide' | 'studentSide';
  isPostViewerClickable?: boolean;
  onClickPostViewer?: (post: PostT) => void;
  cornerActionIcon?: React.ElementType;
  onClickPostCornerAction?: () => void; // 게시글 우상단 버튼 누름
  onClick: () => void; // 피드 내용 진입
  postFeedItem: PostFeedItemT;
  CustomActionsComponent?: ComponentType<{ post: PostT }>;
};

const PostFeed = observer((postFeedProps: PostFeedPropsT) => {
  const { palette } = useTheme();
  const {
    postFeedItem,
    isPostViewerClickable = false,
    onClickPostViewer,
    cornerActionIcon: CornerActionIcon,
    onClickPostCornerAction,
    CustomActionsComponent,
  } = postFeedProps;

  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(ref, {
    // root: document, // 스크롤을 가지는 element
    root: null,
    rootMargin: '0px 0px 300px',
  });
  const isIntersecting = !!entry?.isIntersecting;

  const isTransparent = !postFeedItem.isVisible;
  const showViewCount =
    postFeedProps.perspective === 'teacherSide' &&
    !!postFeedItem.publishedAt &&
    postFeedItem.type !== 'brandPost';

  return (
    <Stack
      ref={ref}
      onClick={postFeedProps.onClick}
      sx={{
        backgroundColor: palette.background.paper,
        opacity: isTransparent ? '50%' : 'inherit',
      }}
    >
      {/* 내용부 */}
      <Stack mx={2} my={2} spacing={1.5}>
        <ExtendedPostTagWrapper post={postFeedItem} />

        <Stack
          direction="row"
          alignItems="center"
          width="100%"
          overflow="hidden"
          sx={{
            '& > :nth-of-type(2)': {
              marginLeft: '8px',
            },
            '& > :nth-of-type(3)': {
              marginLeft: '0px',
            },
          }}
        >
          <Avatar
            src={postFeedItem.writerProfileUrl}
            sx={{ flex: '0 0 auto' }}
          />
          <Stack
            sx={{
              flex: '1 1 auto',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              overflow="hidden"
              sx={{
                overflow: 'hidden',
                flex: '0 1 100%',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: '0 1 auto',
                }}
              >
                {postFeedItem.writerNickname}
              </Typography>
              {postFeedItem.studentNameList?.length ? (
                <>
                  <ArrowForwardIcon
                    sx={{ color: palette.text.secondary, fontSize: '16px' }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      wordBreak: 'keep-all',
                      whiteSpace: 'nowrap',
                      flex: '0 auto',
                    }}
                  >
                    {postFeedItem.studentNameList.length <= 2
                      ? postFeedItem.studentNameList.join(', ')
                      : `${postFeedItem.studentNameList[0]} 등  ${postFeedItem.studentIdList?.length}명`}
                  </Typography>
                </>
              ) : null}
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <AccessTimeIcon
                sx={{ color: palette.text.secondary, fontSize: '16px' }}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                alignItems="center"
              >
                <ContentDateTime
                  date={
                    postFeedItem.publishedAt ||
                    postFeedItem.createdAt ||
                    DUMMY_DATETIME
                  }
                />
              </Typography>
            </Stack>
          </Stack>
          {/* 액션버튼 ... */}
          {CornerActionIcon && onClickPostCornerAction ? (
            <IconButton
              sx={{ flex: '0 0 auto', ml: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                onClickPostCornerAction();
              }}
            >
              <CornerActionIcon />
            </IconButton>
          ) : null}
          {/* 커스텀 액션버튼 */}
          {CustomActionsComponent ? (
            <CustomActionsComponent post={postFeedItem} />
          ) : null}
        </Stack>

        {postFeedItem.title && (
          <Typography variant="subtitle1" sx={{ wordBreak: 'break-all' }}>
            {postFeedItem.title}
          </Typography>
        )}
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ wordBreak: 'break-word' }}
        >
          {!(postFeedItem.summaryImageUrls?.length || postFeedItem.summary) &&
            '본문 내용을 확인해 주세요.'}
          {postFeedItem.summary}
        </Typography>
      </Stack>
      {/* 이미지/동영상 썸네일 */}
      {postFeedItem.summaryImageUrls?.length ? (
        <PostFeedImages
          imageUrls={postFeedItem.summaryImageUrls}
          visible={isIntersecting}
        />
      ) : null}

      {/* 리액션/댓글/조회수 */}
      <Stack direction="row" spacing={1} mx={2} my={1} alignItems="center">
        {showViewCount ? (
          isPostViewerClickable && onClickPostViewer ? (
            <ButtonBase
              aria-label="읽은 사람 보기"
              onClick={(event) => {
                event.stopPropagation();
                onClickPostViewer?.(postFeedItem);
              }}
              sx={{ gap: 1, pr: 1.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
            >
              <VisibilityOutlinedIcon
                sx={{ color: palette.text.secondary, fontSize: '16px' }}
              />
              <NonDefaultStateIndicator show>
                <Typography component="span" variant="body2">
                  {postFeedItem.views ?? 0}명 · 읽은 사람 보기
                </Typography>
              </NonDefaultStateIndicator>
            </ButtonBase>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={(event) => event.stopPropagation()}
              sx={{ cursor: 'default' }}
            >
              <VisibilityOutlinedIcon sx={{ color: palette.text.secondary, fontSize: '16px' }} />
              <Typography variant="body2">{postFeedItem.views ?? 0}명</Typography>
            </Stack>
          )
        ) : null}
        <ChatRoundedIcon
          sx={{ color: palette.text.secondary, fontSize: '16px' }}
        />
        <Typography variant="body2">{postFeedItem.comments || 0}</Typography>
      </Stack>
    </Stack>
  );
});

export default PostFeed;
